import { CheckCircle, Users, Shield, Award, Star, Home } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TrustBadge {
  icon: React.ElementType;
  value: string;
  label: string;
}

// Standard trust badges used across the site
export const standardTrustBadges: TrustBadge[] = [
  {
    icon: CheckCircle,
    value: "7,000+",
    label: "Roofs Completed",
  },
  {
    icon: Users,
    value: "25+",
    label: "Years Experience",
  },
  {
    icon: Shield,
    value: "100%",
    label: "Houston Owned",
  },
];

export const extendedTrustBadges: TrustBadge[] = [
  ...standardTrustBadges,
  {
    icon: Award,
    value: "OC",
    label: "Preferred Contractor",
  },
];

interface TrustBadgesProps {
  badges?: TrustBadge[];
  variant?: "hero" | "strip" | "compact" | "stats";
  className?: string;
  showAnimation?: boolean;
}

/**
 * TrustBadges - Standardized trust indicators component
 *
 * Variants:
 * - hero: Glass-morphic badges for hero sections (dark backgrounds)
 * - strip: Horizontal strip with checkmarks for light backgrounds
 * - compact: Small inline badges
 * - stats: Large animated stat counters
 */
export function TrustBadges({
  badges = standardTrustBadges,
  variant = "hero",
  className,
  showAnimation = true,
}: TrustBadgesProps) {
  if (variant === "hero") {
    return (
      <div
        className={cn(
          "flex flex-col sm:flex-row flex-wrap items-center md:items-start justify-center md:justify-start gap-3 sm:gap-4",
          showAnimation && "animate-fade-in",
          className
        )}
        style={showAnimation ? { animationDelay: "0.3s" } : undefined}
      >
        {badges.map((badge, index) => (
          <div
            key={index}
            className="trust-badge-glass group cursor-default"
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <badge.icon className="w-5 h-5 text-white transition-transform duration-300 group-hover:scale-110" />
            </div>
            <div className="text-left">
              <span className="font-bold text-white block text-sm">{badge.value}</span>
              <span className="text-white/70 text-xs">{badge.label}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "strip") {
    return (
      <div className={cn("flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm", className)}>
        {badges.map((badge, index) => (
          <div key={index} className="flex items-center gap-2 text-muted-foreground">
            <CheckCircle className="w-4 h-4 text-primary" />
            <span>{badge.value} {badge.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className={cn("flex flex-wrap items-center gap-4", className)}>
        {badges.map((badge, index) => (
          <div key={index} className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <badge.icon className="w-4 h-4 text-primary" />
            <span className="font-medium">{badge.value}</span>
            <span>{badge.label}</span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === "stats") {
    return (
      <div className={cn("flex flex-wrap justify-center gap-12 md:gap-16", className)}>
        {badges.map((badge, index) => (
          <div
            key={index}
            className="text-center"
            style={{
              opacity: showAnimation ? 0 : 1,
              transform: showAnimation ? "translateY(10px)" : "none",
              animation: showAnimation ? `fade-in 0.5s ease-out forwards ${index * 100}ms` : "none",
            }}
          >
            <div className="text-4xl md:text-5xl font-heading font-bold text-primary leading-none">
              {badge.value}
            </div>
            <div className="w-8 h-0.5 bg-primary mx-auto mt-2 mb-1" />
            <div className="text-sm text-muted-foreground font-medium">{badge.label}</div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}

// String-based trust indicators (for service page heroes)
export const trustIndicatorStrings = [
  "7,000+ Roofs Completed",
  "Family-Owned",
  "Owens Corning Preferred Contractor",
];

export default TrustBadges;
